import { useParams, Navigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import LegalLayout from "../legal/LegalLayout";
import { GALLERIES } from "../../data/galleries";
import { WHATSAPP } from "../../data/content";

const EASE = [0.22, 1, 0.36, 1];

export default function ServiceGallery() {
    const { slug } = useParams();
    const data = GALLERIES[slug];
    const [idx, setIdx] = useState(null);
    const open = idx !== null;
    const count = data ? data.images.length : 0;

    const close = useCallback(() => setIdx(null), []);
    const prev = useCallback(
        () => setIdx((i) => (i - 1 + count) % count),
        [count],
    );
    const next = useCallback(() => setIdx((i) => (i + 1) % count), [count]);

    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open, close, prev, next]);

    if (!data) return <Navigate to="/" replace />;

    return (
        <LegalLayout>
            <div
                data-testid={`gallery-${slug}`}
                className="mx-auto max-w-[1400px] px-6 pb-28 pt-12 md:px-10 md:pb-40 md:pt-20"
            >
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: EASE }}
                >
                    <span className="eyebrow">{data.eyebrow}</span>
                    <h1
                        className="mt-6 font-serif"
                        style={{
                            fontWeight: 300,
                            lineHeight: 0.98,
                            fontSize: "clamp(2.8rem, 7vw, 5.5rem)",
                        }}
                    >
                        {data.title}
                    </h1>
                    <p
                        className="mt-6 max-w-[48ch] font-sans"
                        style={{
                            color: "var(--muted-foreground)",
                            fontSize: "1rem",
                            lineHeight: 1.8,
                        }}
                    >
                        {data.intro}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
                    className="mt-10 h-px w-full"
                    style={{
                        backgroundColor: "var(--accent)",
                        opacity: 0.7,
                        transformOrigin: "left",
                    }}
                />

                {/* Masonry gallery */}
                {data.images.length > 0 ? (
                    <div className="mt-14 columns-1 gap-5 sm:columns-2 lg:columns-3">
                        {data.images.map((image, i) => (
                            <motion.button
                                key={i}
                                type="button"
                                data-testid={`gallery-item-${i}`}
                                onClick={() => setIdx(i)}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-6% 0px" }}
                                transition={{
                                    duration: 0.8,
                                    ease: EASE,
                                    delay: (i % 3) * 0.05,
                                }}
                                className="group mb-5 block w-full cursor-pointer overflow-hidden border-0 bg-transparent p-0 [break-inside:avoid]"
                            >
                                <img
                                    src={image.src}
                                    alt={image.alt}
                                    loading="lazy"
                                    decoding="async"
                                    className="zoomable h-auto w-full object-cover"
                                />
                            </motion.button>
                        ))}
                    </div>
                ) : (
                    <motion.div
                        data-testid="gallery-empty"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: EASE, delay: 0.3 }}
                        className="flex flex-col items-center justify-center py-28 text-center md:py-40"
                    >
                        <span className="eyebrow">În curând</span>
                        <p
                            className="mt-8 max-w-[24ch] font-serif italic"
                            style={{
                                color: "var(--ink)",
                                opacity: 0.8,
                                fontWeight: 300,
                                lineHeight: 1.25,
                                fontSize: "clamp(1.9rem, 5vw, 3.4rem)",
                            }}
                        >
                            Galeria acestui serviciu se pregătește.
                        </p>
                        <p
                            className="mt-6 max-w-[40ch] font-sans"
                            style={{
                                color: "var(--muted-foreground)",
                                fontSize: "0.98rem",
                                lineHeight: 1.8,
                            }}
                        >
                            Lucrări noi, adăugate în curând. Până atunci, ne poți
                            scrie pentru o programare.
                        </p>
                    </motion.div>
                )}

                {/* Booking CTA */}
                <div
                    className="mt-20 flex flex-col items-start gap-6 border-t pt-10 md:flex-row md:items-center md:justify-between"
                    style={{ borderColor: "var(--line)" }}
                >
                    <p
                        className="font-serif italic"
                        style={{
                            color: "var(--ink)",
                            fontSize: "clamp(1.4rem, 3vw, 2.1rem)",
                            fontWeight: 300,
                        }}
                    >
                        Pregătită pentru o schimbare? ⭐
                    </p>
                    <a
                        href={WHATSAPP}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="gallery-book-btn"
                        className="pill px-8 py-4 font-sans"
                        style={{
                            backgroundColor: "var(--ink)",
                            color: "var(--bg)",
                            fontSize: "0.74rem",
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                        }}
                        onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "var(--accent)")
                        }
                        onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "var(--ink)")
                        }
                    >
                        Programează-te
                    </a>
                </div>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="gallery-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        onClick={close}
                        className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-10"
                        style={{ backgroundColor: "rgba(31,31,31,0.96)" }}
                    >
                        <button
                            type="button"
                            aria-label="Închide"
                            data-testid="lightbox-close"
                            onClick={close}
                            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center"
                            style={{ color: "rgba(252,251,248,0.85)" }}
                        >
                            <X size={26} strokeWidth={1.4} />
                        </button>

                        {count > 1 && (
                            <>
                                <button
                                    type="button"
                                    aria-label="Anterioară"
                                    data-testid="lightbox-prev"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        prev();
                                    }}
                                    className="absolute left-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center md:left-8"
                                    style={{ color: "rgba(252,251,248,0.8)" }}
                                >
                                    <ChevronLeft size={30} strokeWidth={1.3} />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Următoarea"
                                    data-testid="lightbox-next"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        next();
                                    }}
                                    className="absolute right-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center md:right-8"
                                    style={{ color: "rgba(252,251,248,0.8)" }}
                                >
                                    <ChevronRight size={30} strokeWidth={1.3} />
                                </button>
                            </>
                        )}

                        <motion.img
                            key={idx}
                            src={data.images[idx].src}
                            alt={data.images[idx].alt}
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.45, ease: EASE }}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[88vh] max-w-[92vw] object-contain"
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </LegalLayout>
    );
}
